import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, marketsAPI } from '../api';

const FREE_EMAILS = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com','live.com','msn.com'];
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((e||'').trim());
const isValidPhone = (p) => { if(!p) return true; const d = p.replace(/\D/g,''); return d.length >= 10 && d.length <= 15; };
const isFreeEmail = (e) => FREE_EMAILS.includes((e||'').split('@')[1]?.toLowerCase());

export default function RegisterCustomerPage() {
  const [form, setForm] = useState({ email:'', password:'', first_name:'', last_name:'', company:'', phone:'', market_id:'' });
  const [markets, setMarkets] = useState([]);
  const [err, setErr]   = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const nav = useNavigate();

  useEffect(()=>{ marketsAPI.list().then(r=>setMarkets(r.data)).catch(()=>{}); },[]);

  const validateField = (key, value) => {
    switch(key) {
      case 'first_name': return !value.trim() ? 'First name required' : '';
      case 'last_name':  return !value.trim() ? 'Last name required' : '';
      case 'email':
        if (!value) return 'Email required';
        if (!isValidEmail(value)) return 'Invalid email format (e.g. you@company.com)';
        if (isFreeEmail(value)) return 'Please use your company email address';
        return '';
      case 'password': return !value ? 'Password required' : value.length < 8 ? 'Min 8 characters' : '';
      case 'phone': return value && !isValidPhone(value) ? 'Must be at least 10 digits' : '';
      default: return '';
    }
  };

  const upd = k => e => {
    const val = e.target.value;
    setForm(f=>({...f,[k]:val}));
  };

  const blur = k => () => {
    setTouched(t=>({...t,[k]:true}));
    setFieldErrors(fe=>({...fe,[k]:validateField(k,form[k]||'')}));
  };

  const handle = async e => {
    e.preventDefault();
    // Validate all fields on submit
    const keys = ['first_name','last_name','email','password','phone'];
    const errors = {};
    keys.forEach(k => { errors[k] = validateField(k, form[k]||''); });
    setFieldErrors(errors);
    setTouched({ first_name:true, last_name:true, email:true, password:true, phone:true });
    if (Object.values(errors).some(v=>v)) return;
    setErr(''); setBusy(true);
    try {
      const r = await authAPI.customerRegister(form);
      if (r.data.needsVerification) { setDone(true); return; }
      nav('/browse');
    } catch(e) { setErr(e.response?.data?.error || 'Registration failed'); }
    setBusy(false);
  };



  if (done) return (
    <div className="page-container" style={{maxWidth:500,paddingTop:60,textAlign:'center'}}>
      <div style={{fontSize:52,marginBottom:14}}>✉️</div>
      <div className="section-title" style={{marginBottom:8}}>Check Your Email!</div>
      <div style={{color:'#8899AA',fontSize:15,lineHeight:1.7,marginBottom:16}}>
        We sent a verification link to your email. Please click it to activate your account.
      </div>
      <div style={{background:'rgba(109,190,69,0.08)',border:'1px solid rgba(109,190,69,0.2)',borderRadius:8,padding:'14px 16px',marginBottom:28,fontSize:13,color:'#AAB8C8',textAlign:'left'}}>
        ✅ &nbsp;Once verified you can sign in and start hiring professionals.<br/><br/>
        📧 &nbsp;Didn't get the email? Check your spam folder.
      </div>
      <button className="btn btn-primary" onClick={()=>nav('/login')}>Go to Sign In</button>
    </div>
  );

  return (
    <div className="page-container" style={{maxWidth:500,paddingTop:50}}>
      <div className="section-title" style={{marginBottom:4}}>Find a Pro</div>
      <div className="section-sub" style={{marginBottom:28}}>Create your customer account</div>
      {err && <div className="alert alert-error">{err}</div>}
      <form onSubmit={handle} noValidate>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">First Name <span style={{color:'#FF4757'}}>*</span></label>
            <input value={form.first_name} onChange={upd('first_name')} onBlur={blur('first_name')} placeholder="First" />
            {fieldErrors.first_name && touched.first_name && <div style={{fontSize:11,color:'#FF6B7A',marginTop:4}}>⚠ {fieldErrors.first_name}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Last Name <span style={{color:'#FF4757'}}>*</span></label>
            <input value={form.last_name} onChange={upd('last_name')} onBlur={blur('last_name')} placeholder="Last" />
            {fieldErrors.last_name && touched.last_name && <div style={{fontSize:11,color:'#FF6B7A',marginTop:4}}>⚠ {fieldErrors.last_name}</div>}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Work Email <span style={{color:'#FF4757'}}>*</span></label>
          <input type="email" value={form.email} onChange={upd('email')} onBlur={blur('email')} placeholder="you@company.com" />
          {!fieldErrors.email && <div style={{fontSize:11,color:'#556677',marginTop:4}}>Company email required — personal emails not accepted</div>}
          {fieldErrors.email && touched.email && <div style={{fontSize:11,color:'#FF6B7A',marginTop:4}}>⚠ {fieldErrors.email}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Password <span style={{color:'#FF4757'}}>*</span></label>
          <input type="password" value={form.password} onChange={upd('password')} onBlur={blur('password')} placeholder="Min 8 characters" />
          {fieldErrors.password && touched.password && <div style={{fontSize:11,color:'#FF6B7A',marginTop:4}}>⚠ {fieldErrors.password}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Phone</label>
          <input value={form.phone} onChange={upd('phone')} onBlur={blur('phone')} placeholder="+1 (555) 000-0000" />
          {fieldErrors.phone && touched.phone && <div style={{fontSize:11,color:'#FF6B7A',marginTop:4}}>⚠ {fieldErrors.phone}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Company</label>
          <input value={form.company} onChange={upd('company')} placeholder="Your organization" />
        </div>
        <div className="form-group">
          <label className="form-label">Primary Market</label>
          <select value={form.market_id} onChange={upd('market_id')}>
            <option value="">Select market...</option>
            {markets.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <button type="submit" className="btn btn-primary" style={{width:'100%',marginTop:4}} disabled={busy}>
          {busy?<><span className="spinner"/>  Creating...</>:'Create Account →'}
        </button>
      </form>
      <div style={{textAlign:'center',marginTop:18,fontSize:13,color:'#556677'}}>
        Already have an account? <Link to="/login">Sign in</Link>
      </div>
    </div>
  );
}
